import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, Grid, Insight, BarRow } from '../components/UI';
import { useRevenueStats } from '../hooks/useAnalytics';
import { supabase } from '../supabaseClient';
import { MONTHLY_PRICE } from '../constants/pricing';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

function fmtUSD(n) {
  if (n == null) return '…';
  return '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmt(n) { return n == null ? '…' : Number(n).toLocaleString(); }

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

// -----------------------------------------------------------------------------
// useSubscriptionDetail (May 2026) — uses real cancelled_at /
// stripe_subscription_id / cancel_reason columns. The earlier schema-fidelity
// note is gone; the migration "add_abuse_referral_refund_columns_may_2026"
// added them.
//
// Active = currently subscribed: row with cancelled_at IS NULL and
//   expires_at > now and not refunded.
// Cancelled = cancelled_at IS NOT NULL. Counts as churn even if expires_at
//   has not yet passed (Stripe period still running).
//
// Deduplication: when a single stripe_subscription_id has multiple rows
// (renewals create new rows on our side), we collapse them into one
// "subscription" keyed on stripe_subscription_id || (user_id|'fallback').
// MRR counts ONLY distinct active subscriptions × MONTHLY_PRICE (list price).
// -----------------------------------------------------------------------------
function useSubscriptionDetail() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await supabase
          .from('user_passes')
          .select('id,user_id,pass_type,created_at,expires_at,cancelled_at,refunded_at,stripe_subscription_id,stripe_session_id,cancel_reason')
          .in('pass_type', ['MONTHLY', 'monthly'])
          .order('created_at', { ascending: false })
          .limit(1000);
        if (!cancelled) setRows(data || []);
      } catch (_e) {}
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);
  return { rows, loading };
}

export default function Subscriptions() {
  const { data: rev, loading: rL } = useRevenueStats();
  const { rows, loading: subL } = useSubscriptionDetail();

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // ---------------------------------------------------------------------------
  // Step 1 — build per-subscription view. A subscription is identified by
  // stripe_subscription_id when present, else by user_id (legacy/no-sub rows).
  // For each subscription we record: first_active_date, latest_expires_at,
  // cancelled_at, refunded_at, periods count, months paid, current status.
  // ---------------------------------------------------------------------------
  const subMap = {};
  rows.forEach(r => {
    const key = r.stripe_subscription_id || ('user:' + (r.user_id || 'unknown'));
    if (!subMap[key]) {
      subMap[key] = {
        key,
        user_id: r.user_id,
        stripe_subscription_id: r.stripe_subscription_id || null,
        first_active_date: null,
        latest_expires_at: null,
        cancelled_at: null,
        refunded_at: null,
        cancel_reason: null,
        periods: 0,
        monthsPaid: 0,
      };
    }
    const s = subMap[key];
    s.periods++;
    const start = r.created_at ? new Date(r.created_at) : null;
    const end   = r.expires_at ? new Date(r.expires_at) : null;
    if (start && (!s.first_active_date || start < s.first_active_date)) s.first_active_date = start;
    if (end   && (!s.latest_expires_at || end   > s.latest_expires_at)) s.latest_expires_at = end;
    if (r.cancelled_at) {
      const c = new Date(r.cancelled_at);
      if (!s.cancelled_at || c > s.cancelled_at) { s.cancelled_at = c; s.cancel_reason = r.cancel_reason || s.cancel_reason; }
    }
    if (r.refunded_at) {
      const ref = new Date(r.refunded_at);
      if (!s.refunded_at || ref > s.refunded_at) s.refunded_at = ref;
    }
    if (start) {
      const endForCalc = end && end < now ? end : now;
      const months = Math.max(0, (endForCalc - start) / (30 * 86400000));
      s.monthsPaid += months;
    }
  });
  const subs = Object.values(subMap);

  // ---------------------------------------------------------------------------
  // Step 2 — classify
  // ---------------------------------------------------------------------------
  const isActiveNow = (s) => !s.cancelled_at && !s.refunded_at && s.latest_expires_at && s.latest_expires_at > now;
  const cancelledThisMonth = subs.filter(s => s.cancelled_at && s.cancelled_at >= startOfMonth);
  const activeAtStartOfMonth = subs.filter(s => {
    if (!s.first_active_date || s.first_active_date >= startOfMonth) return false;
    if (s.cancelled_at && s.cancelled_at < startOfMonth) return false;
    if (s.refunded_at && s.refunded_at < startOfMonth) return false;
    return true;
  });

  const activeSubs = subs.filter(isActiveNow);
  const mrr = activeSubs.length * MONTHLY_PRICE;
  // Real churn (% of subs active at start-of-month that cancelled this month)
  const churnPctReal = activeAtStartOfMonth.length > 0
    ? Math.round((cancelledThisMonth.length / activeAtStartOfMonth.length) * 1000) / 10
    : 0;

  // Avg tenure (months) — average of (now - first_active_date) for currently-subscribed
  const tenureMonths = activeSubs.length > 0
    ? (activeSubs.reduce((sum, s) => sum + ((now - s.first_active_date) / (30 * 86400000)), 0) / activeSubs.length)
    : 0;

  // LTV per current subscriber based on avg tenure
  const ltv = tenureMonths * MONTHLY_PRICE;

  // Recurring rev last 30 days = sum of monthsPaid in last 30 days × MONTHLY_PRICE
  let recurringRev30 = 0;
  subs.forEach(s => {
    if (!s.first_active_date) return;
    const windowStart = new Date(now.getTime() - 30 * 86400000);
    const periodStart = s.first_active_date < windowStart ? windowStart : s.first_active_date;
    const periodEnd = (s.cancelled_at && s.cancelled_at < now) ? s.cancelled_at
                    : (s.latest_expires_at && s.latest_expires_at < now) ? s.latest_expires_at
                    : now;
    if (periodEnd > periodStart) {
      recurringRev30 += ((periodEnd - periodStart) / (30 * 86400000)) * MONTHLY_PRICE;
    }
  });

  // Cancel-reason breakdown
  const reasonCounts = {};
  cancelledThisMonth.forEach(s => {
    const r = s.cancel_reason || 'Unknown';
    reasonCounts[r] = (reasonCounts[r] || 0) + 1;
  });
  const reasonRows = Object.entries(reasonCounts).sort((a,b) => b[1] - a[1]);
  const maxReason = Math.max(1, ...reasonRows.map(([,c]) => c));

  // Retention buckets
  const buckets = [
    { label: '0-1 month',    min: 0,  max: 1,  count: 0, color: '#dc3545' },
    { label: '1-3 months',   min: 1,  max: 3,  count: 0, color: '#fd7e14' },
    { label: '3-6 months',   min: 3,  max: 6,  count: 0, color: '#9F7E4F' },
    { label: '6-12 months',  min: 6,  max: 12, count: 0, color: '#5a8fa6' },
    { label: '12+ months',   min: 12, max: Infinity, count: 0, color: '#28a745' },
  ];
  subs.forEach(s => {
    const m = s.monthsPaid;
    for (const b of buckets) { if (m >= b.min && m < b.max) { b.count++; break; } }
  });
  const maxBucket = Math.max(1, ...buckets.map(b => b.count));

  // Roster table
  const userRows = subs
    .map(s => ({ ...s, paidUsd: s.monthsPaid * MONTHLY_PRICE, currentActive: isActiveNow(s) }))
    .sort((a, b) => (b.currentActive - a.currentActive) || (b.monthsPaid - a.monthsPaid));

  const loading = rL || subL;

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>
          Subscriptions
        </h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          MONTHLY subscription LTV, churn, and cancel reasons. One-time PASS purchases tracked on Revenue.
        </p>
      </div>

      {/* KPIs */}
      <Grid cols={4}>
        <MetricCard
          label="Active subscribers"
          value={loading ? '…' : fmt(activeSubs.length)}
          sub={loading ? undefined : 'distinct stripe subs'}
          accent="#4a7f96"
        />
        <MetricCard
          label="MRR"
          value={loading ? '…' : fmtUSD(mrr)}
          sub={loading ? undefined : 'active subs × $' + MONTHLY_PRICE + ' list'}
          accent="#314F5C"
        />
        <MetricCard
          label="Avg tenure (mo)"
          value={loading ? '…' : tenureMonths.toFixed(1)}
          sub={loading ? undefined : 'currently subscribed'}
          accent="#5a8fa6"
        />
        <MetricCard
          label="LTV (proj.)"
          value={loading ? '…' : fmtUSD(ltv)}
          sub={loading ? undefined : 'tenure × $' + MONTHLY_PRICE + ' list'}
          accent="#9F7E4F"
        />
      </Grid>

      <Grid cols={3}>
        <MetricCard
          label="Churn this month"
          value={loading ? '…' : churnPctReal + '%'}
          sub={loading ? undefined : fmt(cancelledThisMonth.length) + ' of ' + fmt(activeAtStartOfMonth.length)}
          trend={churnPctReal < 10 ? 'up' : 'down'}
          accent={churnPctReal < 10 ? '#28a745' : churnPctReal < 25 ? '#fd7e14' : '#dc3545'}
        />
        <MetricCard
          label="Recurring rev (30d)"
          value={loading ? '…' : fmtUSD(recurringRev30)}
          sub={loading ? undefined : 'MONTHLY only'}
          accent="#6F8FA7"
        />
        <MetricCard
          label="Cancellations this month"
          value={loading ? '…' : fmt(cancelledThisMonth.length)}
          sub={loading ? undefined : 'with cancelled_at set'}
          accent="#dc3545"
        />
      </Grid>

      {/* Cancel reasons */}
      <Card style={{ marginBottom: 14 }}>
        <CardTitle>Cancel reasons (this month)</CardTitle>
        {loading ? (
          <><Skel /><Skel /></>
        ) : reasonRows.length === 0 ? (
          <Insight type="info">No cancellations this month.</Insight>
        ) : (
          reasonRows.map(([reason, count]) => (
            <BarRow
              key={reason}
              label={reason}
              value={fmt(count)}
              pct={Math.round((count / maxReason) * 100)}
              labelWidth={170}
            />
          ))
        )}
      </Card>

      {/* Retention buckets */}
      <Card style={{ marginBottom: 14 }}>
        <CardTitle>Retention buckets</CardTitle>
        {loading ? (
          <><Skel /><Skel /><Skel /></>
        ) : subs.length === 0 ? (
          <Insight type="info">No MONTHLY subscriptions yet. Bucket data appears after the first paid signup.</Insight>
        ) : (
          buckets.map(b => (
            <BarRow
              key={b.label}
              label={b.label}
              value={fmt(b.count)}
              pct={Math.round((b.count / maxBucket) * 100)}
              labelWidth={110}
            />
          ))
        )}
      </Card>

      {/* Subscriber table */}
      <Card style={{ marginBottom: 14 }}>
        <CardTitle>Subscriber roster ({userRows.length})</CardTitle>
        {loading ? (
          <><Skel /><Skel /><Skel /></>
        ) : userRows.length === 0 ? (
          <Insight type="info">No MONTHLY subscribers in the last 1000 pass rows.</Insight>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.72rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>User ID</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Periods</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Months paid</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>LTV</th>
                  <th style={{ textAlign: 'left',  padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>Cancel reason</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--color-text-secondary)', fontWeight: 500 }}>First start</th>
                </tr>
              </thead>
              <tbody>
                {userRows.slice(0, 50).map(u => {
                  const status = u.refunded_at ? 'refunded' : u.cancelled_at ? 'cancelled' : u.currentActive ? 'active' : 'lapsed';
                  const color = status === 'active' ? '#28a745' : status === 'refunded' ? '#9F7E4F' : '#dc3545';
                  return (
                    <tr key={u.key} style={{ borderBottom: '.5px solid var(--color-border-tertiary)' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '.65rem', color: 'var(--color-text-primary)' }}>
                        {u.user_id ? u.user_id.slice(0, 8) + '…' : '—'}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <span style={{ fontSize: '.6rem', padding: '1px 7px', borderRadius: 8,
                          background: color + '18', color, fontWeight: 500 }}>
                          {status}
                        </span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-primary)' }}>{u.periods}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-primary)' }}>{u.monthsPaid.toFixed(1)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-primary)', fontWeight: 500 }}>{fmtUSD(u.paidUsd.toFixed(0))}</td>
                      <td style={{ padding: '6px 8px', color: 'var(--color-text-secondary)', fontSize: '.65rem' }}>
                        {u.cancelled_at ? (u.cancel_reason || 'Unknown') : '—'}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--color-text-secondary)' }}>
                        {u.first_active_date ? u.first_active_date.toISOString().slice(0, 10) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {userRows.length > 50 && (
              <div style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)', marginTop: 8, textAlign: 'right' }}>
                Showing 50 of {userRows.length}. Full export via Stripe dashboard.
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
